/**
 * Domain enums — single source of truth across api/web/mobile.
 * Mirrors prd/00-foundation/04-data-model.md (`ENUM-*`).
 */

/** SUPER_ADMIN spans all organizations (org switcher); others are provider-scoped. */
export const USER_ROLES = ['OFFICER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Operational status is independent of receipt and refund processing. */
export const BOOKING_AMENDMENT_STATUSES = [
  'PENDING_AGENT',
  'PENDING_PAYMENT',
  'READY_TO_COMMIT',
  'APPLIED',
  'EXPIRED',
  'WITHDRAWN',
  'REJECTED',
  'SUPERSEDED',
] as const;
export type BookingAmendmentStatus = (typeof BOOKING_AMENDMENT_STATUSES)[number];
export const BOOKING_AMENDMENT_AGENT_STAGES = [
  'INCUMBENT',
  'REPLACEMENT_CONSENT',
  'REPLACEMENT_SEARCH',
  'INITIAL_SEARCH',
  'ACCEPTED',
] as const;
export type BookingAmendmentAgentStage = (typeof BOOKING_AMENDMENT_AGENT_STAGES)[number];

/**
 * Login actors. An AGENT is a single mobile actor identified by a `skills` set
 * (`ProviderSkill`); the old DRIVER/CAREGIVER split collapsed into it (a job's
 * required skills come from its service group, not the actor's role).
 */
export const ACTOR_TYPES = ['ORG_USER', 'AGENT', 'PASSENGER'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

/** Actor attribution for a trip cancellation event, including server actions. */
export const CANCELLATION_ACTOR_TYPES = ['PASSENGER', 'AGENT', 'ORG_USER', 'PARTNER', 'SYSTEM'] as const;
export type CancellationActorType = (typeof CANCELLATION_ACTOR_TYPES)[number];

export const ENTITY_STATUSES = ['ACTIVE', 'SUSPENDED'] as const;
export type EntityStatus = (typeof ENTITY_STATUSES)[number];

/**
 * Agent account lifecycle:
 *  - PENDING — self-registration awaiting review.
 *  - ACTIVE — approved and allowed to authenticate.
 *  - REJECTED — application declined; may only be reopened to PENDING.
 *  - SUSPENDED — a formerly active operational account is disabled.
 */
export const DRIVER_STATUSES = ['PENDING', 'ACTIVE', 'REJECTED', 'SUSPENDED'] as const;
export type DriverStatus = (typeof DRIVER_STATUSES)[number];

export const DRIVER_DOCUMENT_TYPES = [
  'DRIVER_LICENSE',
  'PUBLIC_DRIVER_LICENSE',
  'COMPULSORY_MOTOR_INSURANCE',
  'VOLUNTARY_MOTOR_INSURANCE',
  'VEHICLE_PHOTO',
] as const;
export type DriverDocumentType = (typeof DRIVER_DOCUMENT_TYPES)[number];

export const DRIVER_DOCUMENT_REQUIREMENTS = ['REQUIRED', 'OPTIONAL', 'DISABLED'] as const;
export type DriverDocumentRequirement = (typeof DRIVER_DOCUMENT_REQUIREMENTS)[number];

export const VEHICLE_FIELD_NAMES = ['make', 'model', 'color', 'registrationYear'] as const;
export type VehicleFieldName = (typeof VEHICLE_FIELD_NAMES)[number];

export const VEHICLE_FIELD_REQUIREMENTS = ['REQUIRED', 'OPTIONAL'] as const;
export type VehicleFieldRequirement = (typeof VEHICLE_FIELD_REQUIREMENTS)[number];

export const DRIVER_DOCUMENT_OWNER_TYPES = ['AGENT', 'VEHICLE'] as const;
export type DriverDocumentOwnerType = (typeof DRIVER_DOCUMENT_OWNER_TYPES)[number];

export const ATTACHMENT_UPLOAD_STATES = ['RESERVED', 'AVAILABLE', 'ATTACHED', 'REVOKED'] as const;
export type AttachmentUploadState = (typeof ATTACHMENT_UPLOAD_STATES)[number];

/**
 * Thai commercial-bank snapshot for Agent payout-account capture.
 * Source: Bank of Thailand supervised financial institutions list
 * https://www.bot.or.th/en/fi-list.html — retrieved 2026-08-27.
 * `OTHER` keeps the contract usable when the regulated list changes.
 */
export const BANK_CODES = [
  'BBL',
  'KBANK',
  'KTB',
  'TTB',
  'SCB',
  'CIMBT',
  'UOBT',
  'BAY',
  'TISCO',
  'KKP',
  'ICBC',
  'TCRB',
  'LH',
  'BOC',
  'OTHER',
] as const;
export type BankCode = (typeof BANK_CODES)[number];

export const BANK_CODE_SNAPSHOT = {
  sourceUrl: 'https://www.bot.or.th/en/fi-list.html',
  retrievedAt: '2026-08-27',
  banks: {
    BBL: { th: 'ธนาคารกรุงเทพ', en: 'Bangkok Bank' },
    KBANK: { th: 'ธนาคารกสิกรไทย', en: 'Kasikornbank' },
    KTB: { th: 'ธนาคารกรุงไทย', en: 'Krung Thai Bank' },
    TTB: { th: 'ธนาคารทหารไทยธนชาต', en: 'TMBThanachart Bank' },
    SCB: { th: 'ธนาคารไทยพาณิชย์', en: 'Siam Commercial Bank' },
    CIMBT: { th: 'ธนาคารซีไอเอ็มบี ไทย', en: 'CIMB Thai Bank' },
    UOBT: { th: 'ธนาคารยูโอบี', en: 'United Overseas Bank (Thai)' },
    BAY: { th: 'ธนาคารกรุงศรีอยุธยา', en: 'Bank of Ayudhya' },
    TISCO: { th: 'ธนาคารทิสโก้', en: 'Tisco Bank' },
    KKP: { th: 'ธนาคารเกียรตินาคินภัทร', en: 'Kiatnakin Phatra Bank' },
    ICBC: { th: 'ธนาคารไอซีบีซี (ไทย)', en: 'ICBC (Thai)' },
    TCRB: { th: 'ธนาคารไทยเครดิต', en: 'Thai Credit Bank' },
    LH: { th: 'ธนาคารแลนด์ แอนด์ เฮ้าส์', en: 'Land and Houses Bank' },
    BOC: { th: 'ธนาคารแห่งประเทศจีน (ไทย)', en: 'Bank of China (Thai)' },
    OTHER: { th: 'ธนาคารอื่น', en: 'Other bank' },
  },
} as const satisfies {
  sourceUrl: string;
  retrievedAt: string;
  banks: Record<BankCode, { th: string; en: string }>;
};

export const ORG_TYPES = ['STATE_ENTERPRISE', 'PRIVATE', 'GOVERNMENT', 'OTHER'] as const;
export type OrgType = (typeof ORG_TYPES)[number];

export const SERVICE_CATEGORIES = ['WELFARE', 'COMMERCIAL'] as const;
export type ServiceCategory = (typeof SERVICE_CATEGORIES)[number];

export const SERVICE_MODES = ['RIDE_AND_CARE', 'CARE', 'RIDE', 'TRAVEL'] as const;
export type ServiceMode = (typeof SERVICE_MODES)[number];

/** Customer-facing presentation order. Canonical enum/token semantics remain in SERVICE_MODES. */
export const DEFAULT_CUSTOMER_SERVICE_MODE_ORDER = ['TRAVEL', 'CARE', 'RIDE', 'RIDE_AND_CARE'] as const;

export function isCustomerServiceModeOrder(value: unknown): value is ServiceMode[] {
  return (
    Array.isArray(value) &&
    value.length === SERVICE_MODES.length &&
    new Set(value).size === SERVICE_MODES.length &&
    value.every((mode) => SERVICE_MODES.includes(mode as ServiceMode))
  );
}

/** Current welfare balance state, derived from weekly/monthly remaining quota. */
export const ENTITLEMENT_STATUSES = ['AVAILABLE', 'LOW', 'EXHAUSTED', 'UNLIMITED'] as const;
export type EntitlementStatus = (typeof ENTITLEMENT_STATUSES)[number];

/** The bounded period currently closest to exhaustion. */
export const ENTITLEMENT_LIMITING_PERIODS = ['WEEKLY', 'MONTHLY'] as const;
export type EntitlementLimitingPeriod = (typeof ENTITLEMENT_LIMITING_PERIODS)[number];

/** Lifecycle of one welfare entitlement consumption (one row per booking). */
export const ENTITLEMENT_HISTORY_STATUSES = ['CONSUMED', 'RESTORED_CANCELLED', 'RESTORED_MISSED'] as const;
export type EntitlementHistoryStatus = (typeof ENTITLEMENT_HISTORY_STATUSES)[number];

/** Whether entitlement facts came from current rules, a booking snapshot, or legacy inference. */
export const ENTITLEMENT_PROVENANCES = ['CURRENT_DERIVED', 'BOOKING_SNAPSHOT', 'LEGACY_DERIVED'] as const;
export type EntitlementProvenance = (typeof ENTITLEMENT_PROVENANCES)[number];

export const PROVIDER_SKILLS = ['DRIVE', 'CARE'] as const;
export type ProviderSkill = (typeof PROVIDER_SKILLS)[number];

/**
 * Canonical service-mode → required-skill mapping (ADR 0004 / RULE-MODES). A job
 * may only be served by an agent whose `skills` cover these:
 *   RIDE / TRAVEL → {DRIVE} · CARE → {CARE} · RIDE_AND_CARE → {DRIVE, CARE}
 * (one both-skilled agent — there is no driver+caregiver pairing). Single source
 * of truth for both the write-time service-group validation and the dispatch /
 * accept skill gate.
 */
export function requiredSkillsForMode(mode: ServiceMode): ProviderSkill[] {
  switch (mode) {
    case 'CARE':
      return ['CARE'];
    case 'RIDE_AND_CARE':
      return ['DRIVE', 'CARE'];
    default:
      return ['DRIVE']; // RIDE, TRAVEL
  }
}

export const BOOKING_STYLES = ['INSTANT', 'ADVANCE'] as const;
export type BookingStyle = (typeof BOOKING_STYLES)[number];

export const PASSENGER_GROUPS = [
  'CIVIL_SERVANT',
  'GENERAL',
  'CHILD_YOUTH',
  'MEN',
  'UNDERPRIVILEGED',
  'DISABLED',
  'ELDERLY',
  'WOMEN',
] as const;
export type PassengerGroupKey = (typeof PASSENGER_GROUPS)[number];

export const PRICING_MODELS = ['FLAT_PLUS_OVERAGE', 'METERED_TIERS'] as const;
export type PricingModel = (typeof PRICING_MODELS)[number];

/** Special-condition surcharge kinds (both pricing models). */
export const SURCHARGE_KINDS = ['INSTANT', 'TIME_WINDOW'] as const;
export type SurchargeKind = (typeof SURCHARGE_KINDS)[number];

/** How a surcharge amount is applied: absolute baht or a percent of subtotal. */
export const SURCHARGE_AMOUNT_TYPES = ['BAHT', 'PERCENT'] as const;
export type SurchargeAmountType = (typeof SURCHARGE_AMOUNT_TYPES)[number];

/** METERED_TIERS tier rate kind: a one-off FLAT baht, or a PER_UNIT rate × portion. */
export const TIER_RATE_TYPES = ['FLAT', 'PER_UNIT'] as const;
export type TierRateType = (typeof TIER_RATE_TYPES)[number];

export const VEHICLE_TYPES = ['VAN', 'PICKUP', 'AMBULANCE', 'SEDAN', 'OTHER'] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];

export const EQUIPMENT_CATEGORIES = ['MEDICAL', 'GENERAL'] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number];

export const REASON_EVENTS = ['CANCEL', 'REJECT'] as const;
export type ReasonEvent = (typeof REASON_EVENTS)[number];

export const REASON_AUTHORS = ['PROVIDER_STAFF', 'AGENT', 'ORG_USER', 'PASSENGER'] as const;
export type ReasonAuthor = (typeof REASON_AUTHORS)[number];

export const RATING_THRESHOLDS = ['BELOW_3', 'BELOW_4'] as const;
export type RatingThreshold = (typeof RATING_THRESHOLDS)[number];

/** หน่วยงาน sites inside a service area (พื้นที่ของหน่วยงาน). */
export const ORG_SITE_TYPES = ['PARKING', 'TERMINAL', 'OFFICE', 'DEPOT', 'OTHER'] as const;
export type OrgSiteType = (typeof ORG_SITE_TYPES)[number];

export const GENDERS = ['MALE', 'FEMALE'] as const;
export type Gender = (typeof GENDERS)[number];

/** สถานที่โปรด kinds — บ้าน / ที่ทำงาน / ทั่วไป. */
export const FAVORITE_PLACE_KINDS = ['HOME', 'WORK', 'GENERAL'] as const;
export type FavoritePlaceKind = (typeof FAVORITE_PLACE_KINDS)[number];

/** Trip job status — order mirrors the FSM (payment gate is post-assignment). */
export const TRIP_STATUSES = [
  'DRAFT',
  'UNASSIGNED',
  'SCHEDULED',
  'ASSIGNED',
  'AWAITING_PAYMENT',
  'EN_ROUTE_PICKUP',
  'ARRIVED_PICKUP',
  'IN_PROGRESS',
  'ARRIVED_DROPOFF',
  // Care phase (CARE / RIDE_AND_CARE jobs) after drop-off (PRD §5).
  'CARE_GIVING_START',
  'CARE_GIVING_FINISH',
  // Return leg (round-trip jobs) after the outbound/care phase.
  'RETURN_WAITING',
  'RETURN_RIDING',
  'RETURN_DROPPING',
  'COMPLETED',
  'AWAITING_OVERAGE',
  'CANCELLED',
  'MISSED',
] as const;
export type TripStatus = (typeof TRIP_STATUSES)[number];

export const CARE_STATUSES = [
  'UNASSIGNED',
  'SCHEDULED',
  'ASSIGNED',
  'AWAITING_PAYMENT',
  'EN_ROUTE',
  'ARRIVED',
  'CARING',
  'COMPLETED',
  'AWAITING_OVERAGE',
  'CANCELLED',
  'MISSED',
] as const;
export type CareStatus = (typeof CARE_STATUSES)[number];

export const PRESENCES = ['ON_DUTY', 'FREE', 'OFFLINE', 'BREAK', 'UNKNOWN'] as const;
export type Presence = (typeof PRESENCES)[number];

/**
 * Live-tracking presence tabs (FR-W-LIVE-010). Unlike raw `Presence`, WORKING
 * is *derived* — a driver with an active trip counts as working regardless of
 * effective presence; otherwise effective FREE/ON_DUTY map to FREE and all
 * other values map to OFFLINE.
 */
export const TRACKING_BUCKETS = ['WORKING', 'FREE', 'OFFLINE'] as const;
export type TrackingBucket = (typeof TRACKING_BUCKETS)[number];

/** Board groupings over `ENUM-TRIP-STATUS` (FR-W-BOARD-010). */
export const TRIP_BOARDS = ['IN_PROGRESS', 'COMPLETED', 'ADVANCE', 'CANCELLED'] as const;
export type TripBoard = (typeof TRIP_BOARDS)[number];

export const TRIP_BOARD_STATUSES: Record<TripBoard, readonly TripStatus[]> = {
  IN_PROGRESS: [
    'UNASSIGNED',
    'ASSIGNED',
    'AWAITING_PAYMENT',
    'EN_ROUTE_PICKUP',
    'ARRIVED_PICKUP',
    'IN_PROGRESS',
    'ARRIVED_DROPOFF',
    'CARE_GIVING_START',
    'CARE_GIVING_FINISH',
    'RETURN_WAITING',
    'RETURN_RIDING',
    'RETURN_DROPPING',
    'AWAITING_OVERAGE',
  ],
  COMPLETED: ['COMPLETED'],
  ADVANCE: ['SCHEDULED'],
  CANCELLED: ['CANCELLED', 'MISSED'],
};

/**
 * Statuses where the assigned agent is actively servicing the job — drives the
 * per-driver "inProgress" today count and the tracking WORKING bucket.
 * (ASSIGNED / AWAITING_PAYMENT are accepted-but-not-started; SCHEDULED /
 * UNASSIGNED are queued.)
 */
export const TRIP_ACTIVE_STATUSES = [
  'EN_ROUTE_PICKUP',
  'ARRIVED_PICKUP',
  'IN_PROGRESS',
  'ARRIVED_DROPOFF',
  'CARE_GIVING_START',
  'CARE_GIVING_FINISH',
  'RETURN_WAITING',
  'RETURN_RIDING',
  'RETURN_DROPPING',
] as const satisfies readonly TripStatus[];

/**
 * Inverse of TRIP_BOARD_STATUSES: which board a status lives on (deep-links
 * from job cards). Derived so a status re-bucketed in the map can't drift.
 */
export function tripBoardFor(status: TripStatus): TripBoard {
  const board = TRIP_BOARDS.find((b) => TRIP_BOARD_STATUSES[b].includes(status));
  return board ?? 'IN_PROGRESS'; // DRAFT (and future statuses) land on the live board
}

/** Trip legs — a round-trip booking has one trip row per leg. */
export const TRIP_LEGS = ['OUTBOUND', 'RETURN'] as const;
export type TripLeg = (typeof TRIP_LEGS)[number];

/**
 * The fixed positive-tag vocabulary a rating may carry (`ENT-RATING.tags`); labels
 * live in i18n on each surface. A tag outside this set is an operator-authored
 * below-threshold reason label (RULE-RATING-020) and renders verbatim.
 */
export const RATING_TAGS = [
  'polite',
  'professional',
  'punctual',
  'good_driving',
  'attentive',
  'well_dressed',
] as const;
export type RatingTag = (typeof RATING_TAGS)[number];

const RATING_TAG_SET: ReadonlySet<string> = new Set(RATING_TAGS);

/**
 * Whether a stored tag is one of the fixed ids (vs an operator-authored reason label).
 * Set-backed on purpose: `tags` is free text from the apps, so a plain object lookup
 * would resolve inherited keys — `'toString'` would hit `Object.prototype.toString`
 * and a caller keying an i18n map by it would render, or pass along, a function.
 */
export function isRatingTag(value: string): value is RatingTag {
  return RATING_TAG_SET.has(value);
}

/** Canonical timeline event keys (`ENT-STATUS-EVENT.event`); labels live in i18n. */
export const TRIP_EVENTS = [
  'GPS_ARRIVED_PICKUP',
  'GPS_ARRIVED_DROPOFF',
  'SERVICE_REVIEW_REQUIRED',
  'SERVICE_REVIEW_NOT_REQUIRED',
  'AUTOMATION_CANCELLED',
  'OVERAGE_APPROVED',
  'OVERAGE_WAIVED',
  'CREATED',
  'ASSIGNED',
  'ACCEPTED',
  'STARTED',
  'ARRIVED_PICKUP',
  'PICKED_UP',
  'ARRIVED_DROPOFF',
  'CARE_STARTED',
  'CARE_FINISHED',
  'RETURN_STARTED',
  'RETURN_PICKED_UP',
  'RETURN_ARRIVED_DROPOFF',
  'COMPLETED',
  'PAYMENT_VERIFIED',
  'PAYMENT_REJECTED',
  'EDITED',
  'REASSIGNED',
  'AGENT_WITHDREW',
  'CANCELLED',
  // Auto-dispatch exhausted every offer without an accept (RULE-DISPATCH-040) —
  // the trip is terminal MISSED; this event marks the failure on the timeline.
  'DISPATCH_FAILED',
] as const;
export type TripEvent = (typeof TRIP_EVENTS)[number];

export const JOB_CREATOR_TYPES = ['OPERATOR', 'PASSENGER', 'PARTNER'] as const;
export type JobCreatorType = (typeof JOB_CREATOR_TYPES)[number];

/** Creation channel is attribution metadata, independent of the authenticated actor. */
export const JOB_CREATION_CHANNELS = ['WEB', 'MOBILE_APP', 'PARTNER_API', 'UNKNOWN'] as const;
export type JobCreationChannel = (typeof JOB_CREATION_CHANNELS)[number];

/**
 * The mobile agent app's `JobStatus` API values (snake_case) — the vocabulary
 * the agent `GET /jobs` feed emits per job. Mirrors the Flutter `JobStatus`
 * enum's `_byApi` map; the mobile side owns the enum, this constant pins the
 * wire values the API maps trip statuses onto (see `domain/agent.ts`).
 */
export const AGENT_JOB_STATUSES = [
  'pending',
  'scheduled',
  // The operator-owned payment gate: the agent can't advance past it, so it's a
  // non-actionable client state (no forward button) distinct from `picking_up`.
  'awaiting_payment',
  'picking_up',
  'waiting',
  'riding',
  'dropping',
  // After a finish with an outstanding balance: settlement is operator-owned, so
  // this is non-actionable for the agent (distinct from `dropping`).
  'awaiting_overage',
  'care_giving_start',
  'care_giving_finish',
  'return_waiting',
  'return_riding',
  'return_dropping',
  'ended',
  'revoked',
  'rejected',
  'unavailable',
  'canceled',
  'timeout',
] as const;
export type AgentJobStatus = (typeof AGENT_JOB_STATUSES)[number];

// ── Notifications (live status + push) ───────────────────────────────────────
//
// A NOTIFICATION is one user-facing alert delivered over two transports at once:
// the actor's foreground realtime WebSocket (`/realtime/ws`) and a background
// push (FCM, when configured). `NotificationType` is the semantic kind a client
// switches on to route/render; the human-facing title/body are Thai (product
// default). Wire shape is `Notification` in types.ts.
export const NOTIFICATION_TYPES = [
  'NEW_JOB', // an offer was sent to an agent (accept/reject)
  'JOB_CANCELLED', // an agent's offered/assigned job was cancelled
  'DRIVER_CONFIRMED', // a passenger's trip was assigned to an agent
  'DRIVER_NEARBY', // the assigned agent is en route / has arrived at pickup
  'TRIP_COMPLETED', // a passenger's trip finished
  'PAYMENT_REQUEST', // a payment is requested (commercial booking / overage)
  'PAYMENT_VERIFIED', // the operator verified a received payment (FR-W-PAY-010)
  'PAYMENT_REJECTED', // the operator rejected the customer's slip — resubmit (RULE-PAY-030)
  'PAYMENT_SLIP_SUBMITTED', // the customer attached a payment slip — operators verify (to ORG_USER)
  'TRIP_UPDATED', // generic trip status change (catch-all)
  'DISPATCH_FAILED', // auto-dispatch exhausted all offers — no driver found (RULE-DISPATCH-040)
  'NEW_MESSAGE', // a new chat message in one of the actor's trip threads
  'REPORT_EXPORT_COMPLETED', // an asynchronous report CSV is ready for its requester
  'REPORT_EXPORT_FAILED', // an asynchronous report CSV exhausted retries or failed permanently
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** Push-registration owner type — which actor table the device belongs to. */
export const DEVICE_OWNER_TYPES = ['PASSENGER', 'AGENT', 'ORG_USER'] as const;
export type DeviceOwnerType = (typeof DEVICE_OWNER_TYPES)[number];

/** Device platform for push routing (FCM token source). */
export const DEVICE_PLATFORMS = ['IOS', 'ANDROID', 'WEB'] as const;
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

// ── Dispatch offers (RULE-DISPATCH-030/060, FR-D-OFFER-010) ──────────────────
//
// An OFFER is one trip request sent to one agent (driver/caregiver). Unlike the
// PRD's "transient" framing, offers are persisted as `trip_offers` rows so the
// dispatch attempt history is observable (the web simulation page) and assertable
// (tests). The trip itself stays UNASSIGNED ("ค้นหาพนักงาน") while offers are out;
// an ACCEPTED offer is what flips it to ASSIGNED.

/**
 * Lifecycle of a single offer:
 *  - PENDING    — sent, awaiting the agent's response (until `expiresAt`).
 *  - ACCEPTED   — agent accepted → the trip is assigned to them.
 *  - REJECTED   — agent declined (with a reason) → triggers re-dispatch.
 *  - EXPIRED    — no response by `expiresAt` → treated as a decline, re-dispatch.
 *  - SUPERSEDED — another offer for the same trip won (broadcast loser), or the
 *                 operator assigned manually; this offer is no longer actionable.
 *  - CANCELLED  — the trip was cancelled / dispatch aborted while pending.
 */
export const OFFER_STATUSES = [
  'PENDING',
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
  'SUPERSEDED',
  'CANCELLED',
] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];

/** An offer is no longer actionable once it leaves PENDING. */
export const TERMINAL_OFFER_STATUSES: readonly OfferStatus[] = [
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
  'SUPERSEDED',
  'CANCELLED',
];

/**
 * How many agents a trip request fans out to at once:
 *  - SEQUENTIAL — one offer at a time; re-offer to the next on decline/expiry.
 *  - BROADCAST  — up to `dispatchFanout` offers concurrently; first-accept-wins.
 * Backend-configurable per service area (`service_areas.dispatch_strategy`),
 * defaults to SEQUENTIAL.
 */
export const DISPATCH_STRATEGIES = ['SEQUENTIAL', 'BROADCAST'] as const;
export type DispatchStrategy = (typeof DISPATCH_STRATEGIES)[number];

export const DISPATCH_SEARCH_PURPOSES = ['INITIAL', 'REASSIGNMENT'] as const;
export type DispatchSearchPurpose = (typeof DISPATCH_SEARCH_PURPOSES)[number];

export const DISPATCH_SEARCH_STATES = [
  'ACTIVE',
  'ASSIGNED',
  'EXHAUSTED',
  'CANCELLED',
  'SUPERSEDED',
  'LEGACY_HISTORY',
] as const;
export type DispatchSearchState = (typeof DISPATCH_SEARCH_STATES)[number];

export const DISPATCH_OFFER_KINDS = [
  'LEGACY_HISTORY',
  'AUTO_INITIAL',
  'MANUAL_INITIAL',
  'MANUAL_REASSIGNMENT',
] as const;
export type DispatchOfferKind = (typeof DISPATCH_OFFER_KINDS)[number];

/** Fixed, auditable inputs to dispatch-policy v2 ranking. */
export const DISPATCH_POLICY_FACTORS = [
  'PRESENCE',
  'LOCATION_FRESHNESS',
  'DISTANCE',
  'PICKUP_ETA',
  'SCHEDULE_SLACK_BEFORE',
  'SCHEDULE_SLACK_AFTER',
  'WORKLOAD',
  'LAST_ASSIGNED_AGE',
  'ACCEPTANCE_RATE',
  'RATING',
] as const;
export type DispatchPolicyFactor = (typeof DISPATCH_POLICY_FACTORS)[number];

/** V3 replaces coordinate freshness with predictive-origin confidence while
 * retaining the remaining historical factors for audit migration. */
export const DISPATCH_POLICY_V3_FACTORS = [
  'PRESENCE',
  'DISTANCE',
  'PICKUP_ETA',
  'SCHEDULE_SLACK_BEFORE',
  'SCHEDULE_SLACK_AFTER',
  'WORKLOAD',
  'LAST_ASSIGNED_AGE',
  'ACCEPTANCE_RATE',
  'RATING',
  'ORIGIN_CONFIDENCE',
] as const;
export type DispatchPolicyV3Factor = (typeof DISPATCH_POLICY_V3_FACTORS)[number];

export const DISPATCH_ROUTING_MODES = ['PROVIDER', 'COORDINATE'] as const;
export type DispatchRoutingMode = (typeof DISPATCH_ROUTING_MODES)[number];

export const DISPATCH_POLICY_TIE_BREAKERS = ['PICKUP_ETA', 'WORKLOAD', 'LAST_ASSIGNED_AGE'] as const;
export type DispatchPolicyTieBreaker = (typeof DISPATCH_POLICY_TIE_BREAKERS)[number];

export const DISPATCH_POLICY_V3_TIE_BREAKERS = [
  'PICKUP_ETA',
  'DISTANCE',
  'ORIGIN_CONFIDENCE',
  'SCHEDULE_SLACK_BEFORE',
  'SCHEDULE_SLACK_AFTER',
  'WORKLOAD',
  'LAST_ASSIGNED_AGE',
  'ACCEPTANCE_RATE',
  'RATING',
  'DRIVER_ID',
] as const;
export type DispatchPolicyV3TieBreaker = (typeof DISPATCH_POLICY_V3_TIE_BREAKERS)[number];

/** Resolution precedence for policy v2: AREA > PROVIDER > SYSTEM. */
export const DISPATCH_POLICY_SCOPES = ['SYSTEM', 'PROVIDER', 'AREA'] as const;
export type DispatchPolicyScope = (typeof DISPATCH_POLICY_SCOPES)[number];

/** Privacy-safe evidence describing which location source dispatch used. */
export const DISPATCH_TRANSFER_ORIGINS = [
  'PRIOR_ENDPOINT',
  'LIVE',
  'LAST_KNOWN',
  'HOME',
  'NO_COORDINATES',
] as const;
export type DispatchTransferOrigin = (typeof DISPATCH_TRANSFER_ORIGINS)[number];

/** Route provenance persisted in candidate evidence and exposed by Simulator. */
export const DISPATCH_ROUTE_ESTIMATE_SOURCES = [
  'ROUTING',
  'HAVERSINE_FALLBACK',
  'CONSERVATIVE_FALLBACK',
] as const;
export type DispatchRouteEstimateSource = (typeof DISPATCH_ROUTE_ESTIMATE_SOURCES)[number];

/** Non-configurable candidate gates shared by every dispatch surface. */
export const DISPATCH_HARD_ELIGIBILITY_REASONS = [
  'PROVIDER_MISMATCH',
  'DRIVER_INACTIVE',
  'MISSING_REQUIRED_SKILLS',
  'PRESENCE_REQUIRED',
  'LOCATION_REQUIRED',
  'LOCATION_STALE',
  'SERVICE_AREA_MISMATCH',
  'TARGET_TRIP_INELIGIBLE',
  'INVALID_TRIP_SCHEDULE',
  'DISTANCE_OUT_OF_RANGE',
  'ACTIVE_JOB_CONFLICT',
  'TIME_OVERLAP',
  'TRANSFER_ETA_TOO_LONG',
] as const;
export type DispatchHardEligibilityReason = (typeof DISPATCH_HARD_ELIGIBILITY_REASONS)[number];

/** Configurable inclusion/threshold reasons layered after hard eligibility. */
export const DISPATCH_POLICY_EXCLUSION_REASONS = [
  'OFFLINE_NOT_INCLUDED',
  'STALE_LOCATION_NOT_INCLUDED',
  'NO_LOCATION_NOT_INCLUDED',
  'THRESHOLD_NOT_MET',
  'REJECT_COOLDOWN',
] as const;
export type DispatchPolicyExclusionReason = (typeof DISPATCH_POLICY_EXCLUSION_REASONS)[number];

/** Informational evaluation reasons which do not by themselves exclude a candidate. */
export const DISPATCH_CANDIDATE_INFO_REASONS = ['ROUTING_FALLBACK'] as const;
export type DispatchCandidateInfoReason = (typeof DISPATCH_CANDIDATE_INFO_REASONS)[number];

/** The two responses an agent can give to a pending offer. */
export const OFFER_RESPONSE_ACTIONS = ['ACCEPT', 'REJECT'] as const;
export type OfferResponseAction = (typeof OFFER_RESPONSE_ACTIONS)[number];

/**
 * Persisted dispatch intent on a trip (RULE-DISPATCH-070):
 *  - AUTO   — policy-ranked offers start immediately for both instant and
 *             scheduled trips.
 *  - MANUAL — the operator selects a candidate, then the same auditable offer
 *             lifecycle waits for that agent's response before assignment.
 * `null` on the trip means no dispatch path has been chosen yet.
 */
export const DISPATCH_MODES = ['AUTO', 'MANUAL'] as const;
export type TripDispatchMode = (typeof DISPATCH_MODES)[number];

// ── Dashboard & analytics (FR-W-DASH-010 / FR-W-ANALYTICS-010) ───────────────

/**
 * Realtime-dashboard trip tiles. These are *display buckets*, not raw
 * `ENUM-TRIP-STATUS`: `EN_ROUTE_PICKUP` folds every pre-pickup live status
 * (ASSIGNED … ARRIVED_PICKUP), `EN_ROUTE_DROPOFF` folds every post-pickup one
 * (IN_PROGRESS … AWAITING_OVERAGE, incl. care/return phases), and `DELAYED` is
 * derived — a live job past its pickup appointment while still pre-pickup, or
 * past its scheduledEnd unfinished (RULE-METRICS "On-schedule"; the PRD gives
 * no grace threshold, so the comparison is strict). A delayed job counts ONLY
 * in DELAYED, so the live tiles still sum to the header total.
 *
 * MISSED counts today's terminal-missed trips — an instant booking whose
 * auto-dispatch exhausted flips straight to MISSED (`lib/dispatch.ts`), so the
 * tile is date-gated on `statusChangedAt` exactly like COMPLETED/CANCELLED.
 */
export const DASHBOARD_TRIP_BUCKETS = [
  'EN_ROUTE_DROPOFF', // ไปจุดส่งตัว
  'EN_ROUTE_PICKUP', // ไปจุดรับตัว
  'DELAYED', // ล่าช้า
  'COMPLETED', // เสร็จสิ้น
  'CANCELLED', // ยกเลิก
  'MISSED', // พลาดการเดินทาง
  'SCHEDULED', // นัดหมายล่วงหน้า
  'UNASSIGNED', // ยังไม่มอบหมาย
] as const;
export type DashboardTripBucket = (typeof DASHBOARD_TRIP_BUCKETS)[number];

/**
 * Driver-status tiles. BREAK folds into ON_DUTY; UNKNOWN is derived — a driver
 * on a live job whose location ping has gone stale (presence itself is only a
 * self-toggle, so silence while working means "ไม่ทราบสถานะ", not FREE).
 */
export const DASHBOARD_DRIVER_BUCKETS = [
  'ON_DUTY', // ปฏิบัติงาน
  'FREE', // ว่าง
  'OFFLINE', // ออฟไลน์
  'UNKNOWN', // ไม่ทราบสถานะ
] as const;
export type DashboardDriverBucket = (typeof DASHBOARD_DRIVER_BUCKETS)[number];

/** The analytics trend chart's selectable series. */
export const ANALYTICS_TREND_METRICS = ['TRIP_TIME', 'DISTANCE', 'WORK_RATE', 'BY_TYPE'] as const;
export type AnalyticsTrendMetric = (typeof ANALYTICS_TREND_METRICS)[number];

// ── Chat (ข้อความ) — per-trip thread, realtime over WebSocket ─────────────────

/**
 * Canonical chat sender types. A message is authored by exactly one of:
 *  - PASSENGER — the customer.
 *  - DRIVER    — the assigned agent.
 *  - OPERATOR  — an org user on the operator console.
 *  - SYSTEM    — server-generated (booking confirmation, payment request, status
 *                notes). Replaces the legacy 'CENTER'/'ORG_USER' strings.
 * `senderType` derives from the auth actor on POST (PASSENGER→PASSENGER,
 * AGENT→DRIVER, ORG_USER→OPERATOR); SYSTEM is only minted server-side.
 */
export const CHAT_SENDER_TYPES = ['PASSENGER', 'DRIVER', 'OPERATOR', 'SYSTEM'] as const;
export type ChatSenderType = (typeof CHAT_SENDER_TYPES)[number];

/**
 * Chat message kinds. Clients post TEXT / IMAGE / SLIP; PAYMENT_REQUEST and
 * SYSTEM_NOTE are server-generated (SYSTEM sender).
 */
export const CHAT_MESSAGE_KINDS = ['TEXT', 'IMAGE', 'SLIP', 'PAYMENT_REQUEST', 'SYSTEM_NOTE'] as const;
export type ChatMessageKind = (typeof CHAT_MESSAGE_KINDS)[number];

/**
 * Provenance for an actual trip metric. UNKNOWN covers historical rows and
 * incomplete evidence; it must never be presented as a measured value.
 */
export const ACTUAL_METRIC_SOURCES = ['STATUS_EVENTS', 'GPS_TRACE', 'UNKNOWN'] as const;
export type ActualMetricSource = (typeof ACTUAL_METRIC_SOURCES)[number];

/**
 * Source used for a fare-settlement dimension. PLANNED_FALLBACK is billing-only:
 * it preserves the quote when measurement is unavailable and cannot add overage.
 */
export const BILLING_METRIC_SOURCES = [...ACTUAL_METRIC_SOURCES, 'PLANNED_FALLBACK'] as const;
export type BillingMetricSource = (typeof BILLING_METRIC_SOURCES)[number];

/**
 * Payment lifecycle for one {@link PAYMENT_KINDS} row (ENT-PAYMENT):
 *  - PENDING_SLIP    — awaiting the customer's transfer slip (initial + after a
 *                      partial verify re-prompts for the remainder).
 *  - SLIP_SUBMITTED  — a slip is attached, waiting for operator verification.
 *  - VERIFIED        — fully settled (outstanding hit 0, or the operator waived
 *                      the remainder).
 *  - REJECTED        — operator rejected the slip; the customer resubmits.
 *  - WAIVED          — welfare/free trip: nothing to pay (synthetic, no rows).
 *  - REFUND_PENDING  — captured money requires an external refund.
 *  - REFUNDED        — the operator recorded external refund evidence.
 */
export const PAYMENT_STATUSES = [
  'PENDING_SLIP',
  'SLIP_SUBMITTED',
  'VERIFIED',
  'REJECTED',
  'WAIVED',
  'REFUND_PENDING',
  'REFUNDED',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/**
 * Which fare a payment row settles: BASE is the up-front gate (booking /
 * AWAITING_PAYMENT); OVERAGE is billed after completion when actuals exceed the
 * quote (AWAITING_OVERAGE). One row per (trip, kind).
 */
export const PAYMENT_KINDS = ['BASE', 'OVERAGE'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

/** PromptPay recipient identifier selected by a provider. */
export const PROMPTPAY_PROXY_TYPES = ['PHONE', 'TAX_ID'] as const;
export type PromptPayProxyType = (typeof PROMPTPAY_PROXY_TYPES)[number];

// ── Account deletion (PDPA erasure, NFR-SEC-005; App Store / Play review) ────

/** Which mobile account a deletion request targets (subset of ACTOR_TYPES). */
export const DELETION_ACTOR_TYPES = ['PASSENGER', 'AGENT'] as const;
export type DeletionActorType = (typeof DELETION_ACTOR_TYPES)[number];

/**
 * Lifecycle of an account-deletion request (ENT-DELETION-REQUEST):
 *  - PENDING   — submitted OTP-verified on the public web form, awaiting a
 *                SUPER_ADMIN on the web settings queue.
 *  - COMPLETED — the matched account was erased (anonymized in place).
 *  - REJECTED  — declined, with a mandatory resolution note.
 */
export const DELETION_REQUEST_STATUSES = ['PENDING', 'COMPLETED', 'REJECTED'] as const;
export type DeletionRequestStatus = (typeof DELETION_REQUEST_STATUSES)[number];

/** API error codes (api-prd A.0). */
export const ERROR_CODES = [
  'SERVICE_ADMISSION_PAUSED',
  'SERVICE_RESOLUTION_REQUIRED',
  'SERVICE_REVIEW_REQUIRED',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'ACCOUNT_PENDING_APPROVAL',
  'ACCOUNT_APPLICATION_REJECTED',
  'ACCOUNT_SUSPENDED',
  'VALIDATION',
  'ELIGIBILITY_DENIED',
  'ENTITLEMENT_EXHAUSTED',
  'OUT_OF_AREA',
  'INVALID_TRANSITION',
  'TOO_EARLY',
  'DUPLICATE_PHONE',
  'VEHICLE_PLATE_CONFLICT',
  'DISPATCH_FAILED',
  'STAFFING_DEADLINE_PASSED',
  'ACTIVE_DISPATCH_SEARCH',
  'PAYMENT_REQUIRED',
  'OUTSTANDING_PAYMENT',
  'CONFLICT',
  'QUOTE_EXPIRED',
  'BOOKING_RETRY_MISMATCH',
  'DRIVER_TIME_CONFLICT',
  'DRIVER_UNAVAILABLE',
  'TRIP_ALREADY_ASSIGNED',
  'JOB_TAKEN_BY_ANOTHER',
  'OFFER_EXPIRED',
  'OFFER_SUPERSEDED',
  'CANDIDATE_REVISION_STALE',
  'CANDIDATE_REVISION_EXPIRED',
  'DISPATCH_BUDGET_EXHAUSTED',
  'TRIP_ALREADY_STARTED',
  'TRIP_TIME_RANGE_REQUIRED',
  'RATE_LIMITED',
  'STORAGE_UNAVAILABLE',
  'GEOCODING_UNAVAILABLE',
  'UPSTREAM_ERROR',
  'OTP_DELIVERY_FAILED',
  'PROMPTPAY_NOT_CONFIGURED',
  'POLICY_WARNING_CONFIRMATION_REQUIRED',
  'LOCATION_CONFIRMATION_REQUIRED',
  'TRACE_EXPIRED',
  'TRACE_POINT_BUDGET_TOO_SMALL',
  'TRACE_TOO_LARGE',
  'INTERNAL',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];
